import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileSpreadsheet,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Upload,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CardGlowBackground } from '../../components/CardGlowBackground';
import { importSourcesApi } from '../../../api/importSourcesApi';
import { formatModeratorApiKeyError } from '../../../utils/apiClient';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

const GLASS_PANEL =
  'border border-white/50 bg-white/40 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150';

type TabId = 'document' | 'excel';

type ToastApi = { showSuccess: (msg: string) => void; showError: (msg: string) => void };

type PreviewRow = {
  row_number: number;
  url: string;
  title?: string | null;
  source?: string | null;
};

type ImportSourcesCardProps = {
  onImportComplete?: () => void;
};

export function ImportSourcesCard({ onImportComplete }: ImportSourcesCardProps) {
  const { t } = useUiLocale();
  const { showSuccess, showError } = useToast() as ToastApi;

  const [tab, setTab] = useState<TabId>('document');
  const [docUploading, setDocUploading] = useState(false);
  const [docConfirming, setDocConfirming] = useState(false);
  const [docResult, setDocResult] = useState<{
    document_id?: number;
    file_name?: string;
    character_count?: number;
    preview?: string;
    raw_data_id?: number;
    status?: string;
  } | null>(null);

  const [batchId, setBatchId] = useState<number | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [validation, setValidation] = useState<{
    valid_rows?: number;
    invalid_rows?: number;
    duplicate_rows?: number;
    total_rows?: number;
    sample_invalid?: Array<{ row_number?: number; url?: string; error?: string }>;
  } | null>(null);
  const [batchStatus, setBatchStatus] = useState<string | null>(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollBatch = useCallback(
    (id: number) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await importSourcesApi.getUrlListBatch(id);
          setBatchStatus(data.status);
          if (['COMPLETED', 'PARTIAL', 'FAILED'].includes(data.status)) {
            stopPolling();
            if (data.status === 'COMPLETED') {
              showSuccess(t('importSources.toastCrawlDone'));
            } else if (data.status === 'PARTIAL') {
              showSuccess(t('importSources.toastCrawlPartial'));
            } else {
              showError(data.error_message || t('importSources.toastCrawlFailed'));
            }
            onImportComplete?.();
          }
        } catch {
          stopPolling();
        }
      }, 4000);
    },
    [onImportComplete, showError, showSuccess, stopPolling, t],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  const importDocumentFile = async (file: File) => {
    setDocUploading(true);
    setDocConfirming(true);
    setDocResult(null);
    try {
      const { data: uploaded } = await importSourcesApi.uploadDocument(file);
      if (!uploaded.document_id) {
        throw new Error(t('importSources.toastDocFailed'));
      }
      const { data: confirmed } = await importSourcesApi.confirmDocument(uploaded.document_id);
      setDocResult({
        ...uploaded,
        status: confirmed.status,
        raw_data_id: confirmed.raw_data_id,
      });
      showSuccess(t('importSources.toastDocConfirmed'));
      onImportComplete?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        t('importSources.toastDocFailed');
      showError(typeof msg === 'string' ? msg : t('importSources.toastDocFailed'));
    } finally {
      setDocUploading(false);
      setDocConfirming(false);
    }
  };

  const resetExcelFlow = () => {
    setBatchId(null);
    setSheets([]);
    setSelectedSheet('');
    setPreviewRows([]);
    setPreviewMessage(null);
    setValidation(null);
    setBatchStatus(null);
    stopPolling();
  };

  const uploadExcelFile = async (file: File) => {
    setExcelBusy(true);
    resetExcelFlow();
    try {
      const { data } = await importSourcesApi.uploadUrlList(file);
      setBatchId(data.batch_id);
      setSheets(data.sheets || []);
      if (data.sheets?.length) setSelectedSheet(data.sheets[0]);
      showSuccess(t('importSources.toastExcelUploaded'));
    } catch (err: unknown) {
      showError(formatModeratorApiKeyError(err, t('importSources.toastExcelFailed')));
    } finally {
      setExcelBusy(false);
    }
  };

  const handlePreview = async () => {
    if (!batchId || !selectedSheet) return;
    setExcelBusy(true);
    try {
      const { data } = await importSourcesApi.previewUrlList(batchId, {
        sheet_name: selectedSheet,
      });
      setPreviewRows((data.preview_rows || []).filter((r: PreviewRow) => !!r.url?.trim()));
      setPreviewMessage(data.message || null);
      setValidation(null);
      showSuccess(data.message || t('importSources.toastPreviewReady'));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        t('importSources.toastPreviewFailed');
      showError(typeof msg === 'string' ? msg : t('importSources.toastPreviewFailed'));
    } finally {
      setExcelBusy(false);
    }
  };

  const handleValidate = async () => {
    if (!batchId) return;
    setExcelBusy(true);
    try {
      const { data } = await importSourcesApi.validateUrlList(batchId);
      setValidation(data);
      showSuccess(t('importSources.toastValidated'));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        t('importSources.toastValidateFailed');
      showError(typeof msg === 'string' ? msg : t('importSources.toastValidateFailed'));
    } finally {
      setExcelBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!batchId) return;
    setExcelBusy(true);
    try {
      const { data } = await importSourcesApi.confirmUrlList(batchId);
      setBatchStatus(data.status);
      showSuccess(t('importSources.toastCrawlStarted'));
      pollBatch(batchId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        t('importSources.toastConfirmFailed');
      showError(typeof msg === 'string' ? msg : t('importSources.toastConfirmFailed'));
    } finally {
      setExcelBusy(false);
    }
  };

  const tabBtn = (id: TabId, label: string, Icon: typeof FileText) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-medium transition-all ${
        tab === id
          ? 'bg-emerald-500/15 text-emerald-900 ring-1 ring-emerald-200/60 shadow-sm'
          : 'text-slate-600 hover:bg-white/50 hover:text-slate-800'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );

  return (
    <Card className={`relative flex flex-col gap-0 overflow-visible ${GLASS_PANEL} ring-1 ring-emerald-200/40`}>
      <CardGlowBackground variant="emerald" />
      <CardHeader className="relative z-[1] flex-shrink-0 pb-2 pt-4">
        <CardTitle className="flex items-center gap-2 text-base text-slate-900">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/15 ring-1 ring-emerald-200/50">
            <Upload className="h-4 w-4 text-emerald-600" />
          </span>
          {t('importSources.title')}
        </CardTitle>
        <CardDescription className="text-xs text-slate-500">{t('importSources.subtitle')}</CardDescription>
      </CardHeader>

      <CardContent className="relative z-[1] space-y-4 pb-4">
        <div className="flex gap-1 rounded-xl border border-white/60 bg-white/25 p-1">
          {tabBtn('document', t('importSources.tabDocument'), FileText)}
          {tabBtn('excel', t('importSources.tabExcel'), FileSpreadsheet)}
        </div>

        <AnimatePresence mode="wait">
          {tab === 'document' ? (
            <motion.div
              key="doc"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="import-doc" className="text-xs font-medium">
                  {t('importSources.docLabel')}
                </Label>
                <input
                  id="import-doc"
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  disabled={docUploading || docConfirming}
                  className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-500/15 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-emerald-800 hover:file:bg-emerald-500/25 disabled:opacity-60"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setDocResult(null);
                    if (file) void importDocumentFile(file);
                    e.target.value = '';
                  }}
                />
                <p className="text-[11px] text-slate-500">{t('importSources.docHint')}</p>
                {(docUploading || docConfirming) && (
                  <div className="flex items-center gap-2 text-[11px] text-emerald-800">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('importSources.uploadDoc')}
                  </div>
                )}
              </div>

              {docResult && (
                <div className="rounded-lg border border-emerald-200/60 bg-emerald-50/40 px-3 py-2.5 text-xs text-slate-700">
                  <div className="mb-1 flex items-center gap-1.5 font-medium text-emerald-900">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {docResult.file_name}
                  </div>
                  <p className="text-[11px] text-slate-600">
                    {t('importSources.charsExtracted', { n: docResult.character_count ?? 0 })}
                  </p>
                  {docResult.preview && (
                    <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-[11px] text-slate-500">
                      {docResult.preview}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="excel"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="import-excel" className="text-xs font-medium">
                  {t('importSources.excelLabel')}
                </Label>
                <input
                  id="import-excel"
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={excelBusy}
                  className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-500/15 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-emerald-800 hover:file:bg-emerald-500/25 disabled:opacity-60"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    resetExcelFlow();
                    if (file) void uploadExcelFile(file);
                    e.target.value = '';
                  }}
                />
                <p className="text-[11px] text-slate-500">{t('importSources.excelHint')}</p>
                {excelBusy && !batchId && (
                  <div className="flex items-center gap-2 text-[11px] text-emerald-800">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('importSources.uploadExcel')}
                  </div>
                )}
              </div>

              {batchId && (
                <>
                  <Separator />
                  <div className="space-y-1">
                    <Label className="text-[11px]">{t('importSources.sheetLabel')}</Label>
                    <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={t('importSources.selectSheet')} />
                      </SelectTrigger>
                      <SelectContent>
                        {sheets.map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      disabled={excelBusy || !selectedSheet}
                      onClick={handlePreview}
                    >
                      {excelBusy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                      {t('importSources.preview')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      disabled={excelBusy || !previewRows.length}
                      onClick={handleValidate}
                    >
                      {t('importSources.validate')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700"
                      disabled={excelBusy || !validation?.valid_rows}
                      onClick={handleConfirm}
                    >
                      {t('importSources.confirmCrawl')}
                    </Button>
                  </div>

                  {previewMessage && (
                    <p className="text-[11px] text-emerald-800 bg-emerald-50/50 rounded-md px-2 py-1.5 border border-emerald-200/50">
                      {previewMessage}
                    </p>
                  )}

                  {previewRows.length > 0 && (
                    <div className="max-h-36 overflow-auto rounded-lg border border-white/60 bg-white/30 text-[11px]">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-white/80 text-left text-slate-600">
                          <tr>
                            <th className="px-2 py-1">#</th>
                            <th className="px-2 py-1">URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row) => (
                            <tr key={row.row_number} className="border-t border-white/50">
                              <td className="px-2 py-1 text-slate-500">{row.row_number}</td>
                              <td className="truncate px-2 py-1 text-slate-700">{row.url}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {validation && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50/60 text-emerald-800">
                          {t('importSources.validCount', { n: validation.valid_rows ?? 0 })}
                        </Badge>
                        <Badge variant="outline" className="border-amber-200 bg-amber-50/60 text-amber-800">
                          {t('importSources.invalidCount', { n: validation.invalid_rows ?? 0 })}
                        </Badge>
                        <Badge variant="outline" className="border-slate-200 bg-slate-50/60 text-slate-700">
                          {t('importSources.duplicateCount', { n: validation.duplicate_rows ?? 0 })}
                        </Badge>
                      </div>
                      {!!validation.sample_invalid?.length && (
                        <div className="rounded-lg border border-amber-200/60 bg-amber-50/40 px-2.5 py-2 text-[11px] text-amber-900 space-y-1">
                          <p className="font-medium">{t('importSources.invalidSampleTitle')}</p>
                          {validation.sample_invalid.slice(0, 5).map((row, idx) => (
                            <p key={`${row.row_number}-${idx}`} className="truncate">
                              #{row.row_number}: {row.error}
                              {row.url ? ` — ${row.url}` : ''}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {batchStatus === 'CRAWLING' && (
                    <div className="flex items-center gap-2 rounded-lg border border-emerald-200/50 bg-emerald-50/30 px-2.5 py-2 text-xs text-emerald-800">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('importSources.crawling')}
                    </div>
                  )}

                  {batchStatus === 'FAILED' && (
                    <div className="flex items-center gap-2 rounded-lg border border-red-200/50 bg-red-50/30 px-2.5 py-2 text-xs text-red-800">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {t('importSources.crawlFailed')}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
